import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { default as api } from "../../api";
import { dataEncoded, weekFormat, hasOwnProperty, phoneHypen, isDateBetween, expectStyleTag } from "../../utils/lib";

export const mutations = {
    SET_SEARCH_SCHOOL(state, payload) {
        const { data, page } = payload;

        if (state.searchSchool.length === 0 && data.resultCode === 10003) {
            state.isEmpty = true;
            state.searchSchool = [];
            return;
        }

        if (state.searchSchool.length !== 0 && data.resultCode === 10003) {
            state.isLast = true;
            return;
        }

        state.isEmpty = false;
        state.isLast = false;
        state.searchSchool = [...state.searchSchool, ...data.schoolList];
    },

    SET_SCHOOL_CLEAR(state, payload) {
        if (payload === "school") {
            state.isNoticeLast = false;
            state.isNotFound = null;
        }

        if (payload === "search") {
            state.searchSchool = [];
            state.isLast = false;
            state.isEmpty = false;
        }
    },

    SET_BOARD_CLEAR(state, payload) {
        if (payload === "all") {
            state.schoolBoards = {};
            state.schoolBoardsLoading = {};
            state.boardList = [];
            state.pages = {};
        }

        // payload === $route.type
        const board = state.boardList.find(({ id }) => id === payload);

        if (board) {
            state.schoolBoards[board.uid] = [];
            state.schoolBoardsLoading[board.uid] = false;
        }
    },

    SET_PAGE(state, payload) {
        const { type, page } = payload;
        state.pages[type] = page;
    },

    SET_LOADING(state, payload) {
        state.loading[payload] = !state.loading[payload];
    },

    SET_BOARD_LOADING(state, payload) {
        state.schoolBoardsLoading[payload] = !state.schoolBoardsLoading[payload];
    },

    // header 학교섬색 구독 버튼 처리
    SET_AFTER_FOLLOW(state, payload) {
        const { schoolId, type } = payload;
        const searchSchool = [...state.searchSchool];

        if (!schoolId) return;
        if (searchSchool.findIndex(({ id }) => id === schoolId) === -1) return;

        state.searchSchool = searchSchool.reduce((prev, c) => {
            const current = c;
            // payload schoolId
            if (schoolId === current.id) {
                current.isFollowed = type === 2 ? 0 : 1;
            }

            return [...prev, current];
        }, []);
    },

    SET_IS_NOT_FOUND(state, payload) {
        if (payload !== null) {
            state.detail = null;
        }

        state.isNotFound = payload; // message
    },

    SET_IS_NOTICE_LAST(state, payload) {
        state.isNoticeLast = payload;
    },

    SET_SCHOOL(state, payload) {
        if (!payload) {
            state.detail = null;
            state.detailList = [];
            return;
        }

        state.detail = { ...payload, contact: phoneHypen(payload.contact) };
    },

    SET_BOARD(state, payload) {
        const { schoolBoards, schoolBoardsLoading, fixedScroll, pages } = payload;
        state.schoolBoards = schoolBoards;
        state.schoolBoardsLoading = schoolBoardsLoading;
        state.pages = pages;
        state.fixedScroll = fixedScroll;
    },

    SET_BOARDLIST(state, payload = []) {
        state.boardList = payload;
    },

    // detail 구독 버튼 처리
    SET_DETAIL_FOLLOW(state, payload) {
        state.detail.isFollowed = payload;
    },

    SET_SCHOOL_NEWSFEED(state, payload) {
        const { uid, data } = payload;

        if (!uid) return;

        const now = new Date(moment().format("YYYY/MM/DD")).getTime();

        const id = state.boardList.find(obj => obj.uid === uid).id;

        const noticeNumber = id.toString().slice(id.length - 2, id.length);

        const todos = data.feedList
            .map(({ itemType, postDate, boardName, ...rest }) => {
                if (itemType === 3) return null;

                let file = [],
                    image = [];

                const type = noticeNumber === "01" ? "notice" : noticeNumber === "02" ? "airplain" : "mill";

                if (hasOwnProperty(rest, "file")) {
                    file = rest.file;
                }

                const between = isDateBetween(now, postDate);
                const posted_at = between || weekFormat(moment(postDate).format("|E요일 YYYY년 MM월 DD일"));
                const html = expectStyleTag(rest.content);

                return {
                    ...rest,
                    content: html,
                    file,
                    image,
                    boardName,
                    type,
                    posted_at
                };
            })
            .filter(v => v !== null);

        // 중복 렌더링 방지
        if (state.pages[uid] === 1) {
            state.schoolBoards[uid] = todos;
            return;
        }

        state.schoolBoards[uid] = [...state.schoolBoards[uid], ...todos];
    },

    SET_DO_KEEPING(state, payload) {
        const { articleIdx, next, boardId } = payload;

        const { uid } = boardId ? state.boardList.find(v => v.id === boardId) : state.boardList[0];

        const todos = state.schoolBoards[uid];

        const newTodos = todos.reduce((prev, c) => {
            let current = c;
            if (c.articleIdx === articleIdx) {
                current.isKeeped = next;
            }

            return [...prev, current];
        }, []);

        state.schoolBoards[uid] = newTodos;
    },

    SET_FIXED_SCROLL(state, payload) {
        const { type, scroll } = payload;
        state.fixedScroll[type] = scroll;
    }
};

export const actions = {
    async DO_FOLLOW({ commit, dispatch, rootState }, { $alert, type, schoolId, name, boardType, hook }) {
        const { token } = rootState.user;
        const alt = type === 1 ? "을 구독하셨습니다." : "의 구독을 취소하셨습니다.";

        try {
            const data = dataEncoded({ token, type, schoolId });
            await api.school.doFollow(data);

            dispatch("user/GET_SCHOOL_LIST", { loading: true }, { root: true });

            // 특정게시물에서 학교를 팔라우하면 해당 게시물을 다시 랜더링 한다.
            if (boardType) {
                commit("notice/SET_ALL_CLEAR", {}, { root: true });

                if (boardType !== "school") {
                    dispatch("notice/GET_NEWS_FEED", { $alert, type: boardType }, { root: true });
                }
            }

            // 구독 UI 관련 hook
            hook && hook();
            $alert("success", `"${name}" ${alt}`);
        } catch (e) {
            $alert("error", e.message);
        }
    },

    async GET_SEARCH_SCHOOL({ commit, rootState }, { $alert, query, page = 1 }) {
        commit("SET_LOADING", "search");
        const { token } = rootState.user;

        try {
            const response = await api.school.searchSchool({ token, query, page });

            commit("SET_SEARCH_SCHOOL", { data: response.data, page });
            commit("SET_LOADING", "search");
        } catch (e) {
            $alert("error", e.message);
            commit("SET_LOADING", "search");
        }
    },

    // detail에 들어오면 최초 1번 실행
    async GET_SCHOOL_DETAIL({ state, rootState, commit, dispatch }, { $alert, $router, schoolId, boardId }) {
        commit("SET_LOADING", "detail");
        const { token } = rootState.user;

        try {
            const response = await api.school.getSchoolDetail({ token, schoolId });

            if (response.data.resultCode === 10003) {
                commit("SET_IS_NOT_FOUND", "찾을 수 없는 학교/게시물 입니다.");
                commit("SET_LOADING", "detail");
                return;
            }

            const boardList = response.data.boardList || [];

            if (!boardId) {
                $router.push(`/school/${schoolId}/${boardList[0].id}`);
                commit("SET_LOADING", "detail");
                return;
            }

            const schoolBoards = {};
            const schoolBoardsLoading = {};
            const schoolBoardList = [];
            const schoolBoardUid = [];
            const pages = {};
            const fixedScroll = {};

            for (let i = 0; i < boardList.length; i++) {
                const uid = uuidv4();
                schoolBoardUid.push(uid);
                schoolBoards[uid] = [];
                schoolBoardsLoading[uid] = false;
                pages[uid] = 1;
                fixedScroll[uid] = 0;
                schoolBoardList.push({
                    ...boardList[i],
                    uid
                });
            }

            const isIndex = boardId && boardList.findIndex(v => v.id === boardId);

            const index = !isIndex || isIndex === -1 ? 0 : isIndex;

            const uid = schoolBoardUid[index];

            if (!uid) {
                commit("SET_IS_NOT_FOUND", "찾을 수 없는 학교/게시물 입니다.");
                commit("SET_LOADING", "detail");
                return;
            }

            commit("SET_BOARD", { schoolBoards, schoolBoardsLoading, fixedScroll, pages });
            commit("SET_BOARDLIST", schoolBoardList); // 메뉴 리스트 set
            commit("SET_BOARD_LOADING", uid); // notice loading 시작
            commit("SET_SCHOOL", { ...response.data, schoolId });

            const params = {
                $alert,
                schoolId,
                boardId: boardId || schoolBoardList[0].id,
                uid,
                loading: false
            };

            // 첫번째 게시물 가져오기
            dispatch("GET_SCHOOL_NEWS_FEED", params);
            commit("SET_LOADING", "detail"); // detail 로딩 끝
        } catch (e) {
            commit("SET_LOADING", "detail");
            $alert("error", e.message);
        }
    },

    // detail 에서 infinite scroll 할 때 주로 사용됨 ( 최초 1번은 무조건 실행 )
    async GET_SCHOOL_NEWS_FEED({ commit, rootState, state }, { $alert, schoolId, boardId, uid, loading }) {
        const { token } = rootState.user;
        const { pages } = state;

        // 최초시 GET_SCHOOL_DETAIL 에서 이미 로딩을 진행해서 loading이 false 여야 할때가 있음
        if (loading) {
            commit("SET_BOARD_LOADING", uid);
        }

        const params = {
            token,
            page: pages[uid],
            schoolId,
            boardId,
            categoryId: 0,
            getTypeId: "board"
        };

        try {
            const response = await api.notice.getNewsfeed(params);

            if (response.data.resultCode === 10003) {
                commit("SET_IS_NOTICE_LAST", true);
            } else {
                commit("SET_SCHOOL_NEWSFEED", { data: response.data, uid });
            }

            commit("SET_BOARD_LOADING", uid);
        } catch (e) {
            commit("SET_BOARD_LOADING", uid);
            $alert("error", e.message);
        }
    }
};
